"use client";

import Button from "./ui/Button";
import { motion } from "motion/react";
import { useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import Link from "next/link";
import posthog from "posthog-js";

const variant = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.5 },
  },
};

export default function HeroSection() {
  const [videoClicked, setVideoClicked] = useState(false);

  return (
    <section className="relative my-20 w-full overflow-x-hidden px-6 md:my-30 lg:mt-40 lg:mb-30">
      <div className="relative z-10 mx-auto flex flex-col items-center justify-around gap-6">
        <div className="flex flex-col gap-6 font-bold md:items-center lg:justify-center">
          <motion.h2
            className="text-primary text-center text-3xl leading-[1] font-bold uppercase md:text-4xl lg:text-5xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.5 }}
          >
            About Remotecoders
          </motion.h2>{" "}
          <motion.div
            className="flex w-full"
            variants={variant}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="bg-primary relative aspect-video w-full overflow-hidden rounded-2xl lg:max-w-180">
              <div
                className={cn(
                  "before:bg-primary/20 relative h-full w-full overflow-hidden before:absolute before:inset-0 before:top-0 before:z-10 before:mix-blend-color",
                  {
                    hidden: videoClicked,
                  },
                )}
              >
                <div
                  className="absolute top-1/2 left-1/2 z-10 size-25 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                  onClick={() => {
                    setVideoClicked(true);
                    posthog.capture('hero_video_played');
                  }}
                >
                  <Image src="/icons/play-btn.svg" alt="play button" fill />
                </div>
                <Image
                  fill
                  src="/video-placeholder.webp"
                  alt="video placeholder"
                  className="h-full w-full scale-115 object-cover"
                  sizes="(max-width: 460px) 100vw, (max-width: 1024px) 100vw, 50vw"
                  loading="lazy"
                />
              </div>
              <div className="w-[calc(100vw - 48px)] aspect-video md:max-w-180 lg:w-screen">
                {videoClicked && (
                  <video
                    src="https://res.cloudinary.com/dpywcbkau/video/upload/q_auto/Remotecoders_ftbocj.mp4"
                    controls
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
            </div>
          </motion.div>
          <p className="z-1 w-full max-w-175 text-center text-xl font-normal">
            Remotecoders connects European companies with top tech talent.{" "}
            <strong>
              We handpick skilled talents to serve clients and mentor future
              trainees.
            </strong>
          </p>
          <Link href="/about">
            <Button className="z-1 w-full self-center md:max-w-max">
              More about us
            </Button>
          </Link>
        </div>
      </div>
      <div className="absolute inset-0 z-1 hidden lg:block">
        <Image
          src="/shapes/group-camera.svg"
          alt="camera illustration"
          width={90}
          height={90}
          className="absolute top-20 left-1/2 -translate-x-150 -translate-y-1/2"
          loading="lazy"
        />
        <Image
          src="/shapes/group-48.svg"
          alt="camera illustration"
          width={60}
          height={60}
          className="absolute top-60 left-1/2 translate-x-120 -translate-y-1/2"
          loading="lazy"
        />
        <Image
          src="/shapes/group-41.svg"
          alt="camera illustration"
          width={165}
          height={165}
          className="absolute bottom-0 left-0 -translate-x-1/2 -translate-y-1/2"
          loading="lazy"
        />
        <Image
          src="/shapes/group-42.svg"
          alt="camera illustration"
          width={120}
          height={120}
          className="absolute top-24 right-0 translate-x-1/2 -translate-y-1/2"
          loading="lazy"
        />
        <Image
          src="/shapes/group-50.svg"
          alt="camera illustration"
          width={120}
          height={120}
          className="absolute bottom-6 left-1/2 translate-x-130 -translate-y-1/2"
          loading="lazy"
        />
      </div>
    </section>
  );
}
